import Swiper from 'swiper';
import { A11y, EffectCoverflow } from 'swiper/modules';
import { ScrollCaptain } from 'scroll-captain';
import { getOffset } from '../../utilities/js/helper';
import './layer-teaser.scss';

class LayerTeaser {
    constructor (element, options) {
        // Default options
        const defaults = {
            initAttr: 'data-layerteaser',
            prevSlideAttr: 'data-layerteaser-prev',
            nextSlideAttr: 'data-layerteaser-next',
            stickyValue: 'sticky',
            layerValue: 'layer',
            scrollTriggerValue: 'scroll-trigger',
            containerValue: 'container',
            breakpoint: 768 // Minimum viewport width for different behavior
        };

        // Settings
        this.settings = Object.assign({}, defaults, options);
        
        // DOM elements
        this.$root = element;
        this.$container = this.$root.querySelector(`[${this.settings.initAttr}="${this.settings.containerValue}"]`);
        this.$$layers = this.$root.querySelectorAll(`[${this.settings.initAttr}="${this.settings.layerValue}"]`);
        this.$$srNavButtons = this.$root.querySelectorAll(`[${this.settings.prevSlideAttr}], [${this.settings.nextSlideAttr}]`);
        this.$nextSrButton = null;
        
        // Variables
        this.breakpoint = window.matchMedia(`(min-width: ${this.settings.breakpoint}px)`);
        this.breakpointActive = this.breakpoint.matches;
        this.animation = null;
        this.slider = null;
        this.highestHeight = 0;
        this.progress = 0;
        this.activeSlideIndex = 0;
    }

    initialize () {  
        // Initialize the component if there are layers to process
        if (this.$$layers.length > 0) {
            this.setLayersHeight();
            this.initializeSlider();
            this.loadAnimation();
            this.setEvents();
        }
    }   

    setLayersHeight () {
        this.highestHeight = 0;

        // Find the highest layer to set a consistent minimum height
        this.$$layers.forEach(($layer, index) => {
            const height = $layer.clientHeight;                              
            
            if (height > this.highestHeight) {                    
                this.highestHeight = height;
            }
        }); 

        // Apply the highest layer height as a CSS variable
        this.$root.style.setProperty('--layer-min-height', `${this.highestHeight}px`);
    }

    initializeSlider() {
        const setSlideStates = (activeIndex) => {
            // Update slide state classes for visual effects
            this.$root.style.setProperty('--active-index', activeIndex + 1);
            this.$$layers.forEach(($layer, index) => {   
                if (index < activeIndex - 1) {
                    $layer.classList.add('swiper-slide-before');
                } else {
                    $layer.classList.remove('swiper-slide-before');
                }  
                
                if (index > activeIndex + 1) {
                    $layer.classList.add('swiper-slide-after');
                } else {
                    $layer.classList.remove('swiper-slide-after');
                }  
            });
        };

        const improveAccessibility = (activeIndex) =>  {
            // Update accessibility attributes for the active slide
            this.$$layers.forEach(($layer, index) => {
                $layer.setAttribute('aria-hidden', index !== activeIndex);
                index === activeIndex ? $layer.removeAttribute('inert') : $layer.setAttribute('inert', '');
            });
        
            // Focus the next screen reader button if applicable
            this.$nextSrButton?.focus();
            this.$nextSrButton = null;
        }; 

        // Initialize Swiper with custom configurations
        this.slider = new Swiper(this.$container, {
            modules: [A11y, EffectCoverflow],
            autoHeight: true,
            speed: 800,
            slidesPerView: 1,
            effect: 'coverflow',
            grabCursor: false,
            allowTouchMove: false,
            simulateTouch: false,
            navigation: false,
            pagination: false,
            keyboard: false,
            mousewheel: false,
            coverflowEffect: {
                rotate: 0,
                stretch: 0,
                depth: 100,
                modifier: 1,
                slideShadows: false,
            },
            direction: 'vertical',
            on: {
                init: (swiper) => {
                    improveAccessibility(swiper.activeIndex);
                    setSlideStates(swiper.activeIndex);
                },
                slideChange: (swiper) => {
                    setSlideStates(swiper.activeIndex);
                },
                slideChangeTransitionEnd: (swiper) => {
                    improveAccessibility(swiper.activeIndex);
                }
            }
        });
    }

    updateSlider () {
        // Update the slider's active index and apply scrolling effect class
        if (this.activeSlideIndex !== this.slider.activeIndex) {
            if (this.activeSlideIndex < this.slider.activeIndex) {
                this.$root.classList.add('layer-teaser--scrolling-up');
            } else {
                this.$root.classList.remove('layer-teaser--scrolling-up');
            }

            this.slider.slideTo(this.activeSlideIndex, 800);
        }
    }

    loadAnimation () {   
        const calculateActiveSlideIndex = () => {
            // Determine the active slide based on progress
            this.$$layers.forEach(($layer, index) => {
                if (this.progress >= (1 / this.$$layers.length) * (index)) {                
                    this.activeSlideIndex = index; 
                }
            });
        };

        // Initialize ScrollCaptain for managing scrolling animations
        this.animation = new ScrollCaptain(this.$root, {
            initAttr: this.settings.initAttr,
            triggerSuffix: this.settings.scrollTriggerValue,
            triggerPosition: 0.5,
            bottom: '-50%',
            top: `${this.$$layers.length * -135}%`,
            cssSpace: 'sticky-spacing',
            animateSticky: {
                targetSuffix: this.settings.stickyValue,
                sticky: true
            },
            onInit: (progress) => {
                this.progress = progress; 
                calculateActiveSlideIndex();
                this.updateSlider();
            },
            updateOnResize: () => {
                // Update breakpoint state on window resize
                this.breakpointActive = this.breakpoint.matches;
            },
            onScroll: (progress) => {                                
                this.progress = progress; 
                calculateActiveSlideIndex();
                this.updateSlider();
            }
        });
    }

    setEvents () {        
        // Attach click events to screen reader navigation buttons
        this.$$srNavButtons.forEach(($srNavButton, index) => {            
            $srNavButton.addEventListener('click', () => {
                const $parentLayer = $srNavButton.closest(`[${this.settings.initAttr}="${this.settings.layerValue}"]`);

                if ($parentLayer) {
                    const layerIndex = Array.from(this.$$layers).indexOf($parentLayer);
    
                    if ($srNavButton.hasAttribute(this.settings.prevSlideAttr)) {
                        this.activeSlideIndex = layerIndex - 1;

                        if (this.$$srNavButtons[index - 1]) {
                            this.$nextSrButton = this.$$srNavButtons[index - 1];
                        }
                    } else if ($srNavButton.hasAttribute(this.settings.nextSlideAttr)) {
                        this.activeSlideIndex = layerIndex + 1;  
                        if (this.$$srNavButtons[index + 1]) {
                            this.$nextSrButton = this.$$srNavButtons[index + 1];
                        }
                    }

                    this.updateSlider(); 
                }
            });
        });        

        // Handle custom event for detecting programmatic scrolling
        window.addEventListener('programmaticScrollDetected', () => {
            const rootPosition = getOffset(this.$root);
            const scrollPosition = window.scrollY || document.documentElement.scrollTop;
    
            if (this.progress === 0 && rootPosition.top - scrollPosition < 0 && this.animation !== null) {                
                this.animation.update();
            }
        });
    }
}

export { LayerTeaser };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $$roots = $context.querySelectorAll('[data-layerteaser="root"]');
        $$roots.forEach($root => {
            const $rootAPI = new LayerTeaser($root);
            $rootAPI.initialize();
        });
    }
});
